<template>
  <TimeBanner
      :type="bannerType"
      :show="showBanner"
      :package-status="packageStatus"
      @close="handleBannerClose"
      @upgrade-clicked="openPricingModal"
  />

  <PricingPacketModal
      :isVisible="isPricingModalVisible"
      :urgency="pricingModalUrgency"
      :packages="availablePackages"
      :userPackageStatus="packageStatus"
      @close="closePricingModal"
      @package-upgraded="onPackageUpgraded"
  />

  <PaymentCard
      :isVisible="isPaymentModalVisible"
      :selectedPackage="selectedPackageForPayment"
      :isAnnual="true"
      @close="closePaymentModal"
      @payment-success="onPaymentSuccess"
  />

  <div class="header-container">
    <div class="header-view no-text-select" :class="{ 'is-scrolled': isScrolled }">
      <div class="left">
        <img :src="companyLogo" alt="Logo" class="logo" @click="goToHome" />
        <div class="horizontal-menu">
          <router-link to="/" class="route-link">
            <div class="horizontal-menu-item" :class="{'active': checkActive('/')}">
              <span>{{$t('homePage')}}</span>
            </div>
          </router-link>
          <router-link to="/programs" class="route-link">
            <div class="horizontal-menu-item" :class="{'active': checkActive('/programs')}">
              <span>{{$t('programs_title')}}</span>
            </div>
          </router-link>
          <router-link to="/tests" class="route-link">
            <div class="horizontal-menu-item" :class="{'active': checkActive('/tests')}">
              <span>{{$t('tests_title')}}</span>
            </div>
          </router-link>
          <router-link to="/game-labs" class="route-link">
            <div class="horizontal-menu-item" :class="{'active': checkActive('/game-labs')}">
              <span>{{$t('gameLabs_title')}}</span>
            </div>
          </router-link>
          <router-link to="/blogs" class="route-link">
            <div class="horizontal-menu-item" :class="{'active': checkActive('/blogs')}">
              <span>{{$t('blogs_titles')}}</span>
            </div>
          </router-link>
          <router-link to="/users" class="route-link">
            <div class="horizontal-menu-item" :class="{'active': checkActive('/users')}">
              <span>{{$t('users_tab')}}</span>
            </div>
          </router-link>
          <router-link to="/journeys" class="route-link">
            <div class="horizontal-menu-item" :class="{'active': checkActive('/journeys')}">
              <span>{{$t('journeys_name')}}</span>
            </div>
          </router-link>
          <router-link to="/marketplace" class="route-link">
            <div class="horizontal-menu-item" :class="{'active': checkActive('/marketplace')}">
              <span>{{$t('marketplace_name')}}</span>
            </div>
          </router-link>
        </div>
      </div>
      <div class="center"></div>
      <div class="right">
        <div id="step-2" class="company-dropdown-wrapper" v-click-outside="hideCompanyDropdown">
          <div class="company-dropdown" @click="toggleCompanyDropdown" v-if="!companyLoading">
            <span>{{ companyName }}</span>
            <img src="../assets/images/arrowdown.svg" class="arrow-down" :class="companyDropdown && 'rotate'"/>
          </div>
          <div class="dropdown-loading-wrapper" v-if="companyLoading">
            <VueSpinnerIos size="14" color="#000000"/>
          </div>
          <div class="dropdown-content" v-if="companyDropdown">
            <div class="search-box" v-if="searchEnabled">
              <input type="text" class="search-input" v-model="searchQuery" :placeholder="$t('search')" @input="filterCompanyItems" ref="searchInput" />
            </div>
            <div class="list scroll-design">
              <div class="item" v-for="item in filteredCompanyItems" @click="changeCompany(item)">
                <span>{{ item.name }}</span>
              </div>
              <div class="no-results" v-if="filteredCompanyItems.length === 0 && searchQuery">
                <span>{{ $t('no_results_found') }}</span>
              </div>
            </div>
          </div>
        </div>
        <NotificationModal id="step-4" />
        <div class="menu-icon no-text-select" @click="handleGoMessages">
          <img src="@/assets/images/messageSingleOutline.svg" width="18" height="18"/>
        </div>
        <div class="menu-wrapper" v-click-outside="handleMenuClickOutside">
          <div class="menu-icon no-text-select" @click="toggleMenu">
            <img src="@/assets/images/menu.svg" width="20" height="20"/>
          </div>
          <Transition name="slide-fadeUp" appear>
            <div class="menu-view" v-if="isMenuOpen">
              <div class="menu-user">
                <div class="menu-user-left">
                  <img :src="loginUser?.avatarUrl" class="user-avatar"/>
                </div>
                <div class="menu-user-right">
                  <span class="menu-user-name">{{`${loginUser?.name} ${loginUser?.surname}`}}</span>
                  <span class="menu-user-email">{{loginUser?.email}}</span>
                </div>
                <div class="menu-user-end">
                  <a href="https://ibilityappsupport.zendesk.com/" target="_blank">
                    <div class="menu-icon">
                      <img src="@/assets/images/supportDarkOutline.svg" width="16" height="16"/>
                    </div>
                  </a>
                  <router-link to="/settings">
                    <div class="menu-icon">
                      <img src="@/assets/images/settingsDarkOutline.svg" width="16" height="16"/>
                    </div>
                  </router-link>
                </div>
              </div>
              <div class="menu-row">
                <div class="menu-row-left">
                  <div class="menu-item-header">
                    <span>{{$t('menu_content')}}</span>
                  </div>
                  <template v-for="item in getMenuRoutes('content')">
                    <router-link :to="item.path" class="route-link">
                      <div class="menu-item" :class="{'active': checkActive(item.path)}">
                        <img v-if="checkActive(item.path)" :src="item.activeIcon" :alt="item.name" />
                        <img v-else :src="item.icon" :alt="item.name" />
                        <span>{{ item.name }}</span>
                      </div>
                    </router-link>
                  </template>
                  <div class="menu-item-header">
                    <span>{{$t('menu_app')}}</span>
                  </div>
                  <template v-for="item in getMenuRoutes('app')">
                    <router-link :to="item.path" class="route-link">
                      <div class="menu-item" :class="{'active': checkActive(item.path)}">
                        <img v-if="checkActive(item.path)" :src="item.activeIcon" :alt="item.name" />
                        <img v-else :src="item.icon" :alt="item.name" />
                        <span>{{ item.name }}</span>
                      </div>
                    </router-link>
                  </template>
                </div>
                <div class="menu-row-right">
                  <div class="menu-item-header">
                    <span>{{$t('menu_tracking')}}</span>
                  </div>
                  <template v-for="item in getMenuRoutes('tracking')">
                    <router-link :to="item.path" class="route-link">
                      <div class="menu-item" :class="{'active': checkActive(item.path)}">
                        <img v-if="checkActive(item.path)" :src="item.activeIcon" :alt="item.name" />
                        <img v-else :src="item.icon" :alt="item.name" />
                        <span>{{ item.name }}</span>
                      </div>
                    </router-link>
                  </template>
                  <div class="menu-item-header">
                    <span>{{$t('menu_sales')}}</span>
                  </div>
                  <template v-for="item in getMenuRoutes('sales')">
                    <router-link :to="item.path" class="route-link">
                      <div class="menu-item" :class="{'active': checkActive(item.path)}">
                        <img v-if="checkActive(item.path)" :src="item.activeIcon" :alt="item.name" />
                        <img v-else :src="item.icon" :alt="item.name" />
                        <span>{{ item.name }}</span>
                      </div>
                    </router-link>
                  </template>
                </div>
              </div>
              <div class="menu-splitter" />
              <div class="menu-footer">
                <TranslationDropdown id="step-3" :expand-to-left="true" :show-warning="false" :selected-language="$i18n.locale" :on-select="updateLanguage"/>
                <div class="logout-button" @click="onLogout">
                  <span>{{ $t('logout_profile') }}</span>
                </div>
              </div>
            </div>
          </Transition>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import NotificationModal from "@/components/modals/NotificationModal.vue";
import ActionDropdown from "@/components/ActionDropdown.vue";
import TranslationDropdown from "@/components/TranslationDropdown.vue";
import TimeBanner from "@/components/TimeBanner.vue";
import companyService from "@/service/companyService";
import pricingService from "@/service/pricingService";
import {handleErrorResponse} from "@/utils/utils";
import {VueSpinnerIos} from "vue3-spinners";
import ibilityLogo from "@/assets/images/iblity_black.svg";
import settingsService from "@/service/settingsService";
import authService from "@/service/authService";
import menuRoutes from "../router/menuRoutes";
import roleService from "@/service/roleService";
import PricingPacketModal from "@/components/modals/PricingPacketModal.vue";
import PaymentCard from "@/components/modals/PaymentCardModal.vue";
import response from "vuedraggable";

export default {
  name: 'Header',
  components: {
    VueSpinnerIos,
    TranslationDropdown,
    ActionDropdown,
    NotificationModal,
    TimeBanner,
    PricingPacketModal,
    PaymentCard
  },
  data() {
    return {
      isMenuOpen: false,
      companyDropdown: false,
      companyLoading: true,
      companyItems: [],
      searchQuery: '',
      filteredCompanyItems: [],
      searchEnabled: true,
      companyName: '',
      companyLogo:  ibilityLogo,
      languages: [
        { name: 'English', code: 'en' },
        { name: 'Türkçe', code: 'tr' },
      ],
      allowedIds: [],
      loginUser: localStorage.getItem('user') ? JSON.parse(localStorage.getItem('user')) : {},
      menuRoutes,
      showBanner: false,
      bannerType: null,
      packageStatus: null,
      bannerCheckInterval: null,
      isPricingModalVisible: false,
      isPaymentModalVisible: false,
      pricingModalUrgency: 'normal',
      availablePackages: [],
      selectedPackageForPayment: null
    }
  },
  props: {
    isScrolled: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    computedBannerType() {
      // packageStatus.show=true VE backend bir urgency gönderdiyse → o banner type
      // (trial-ending, payment-reminder vb. — urgency='normal' olsa bile demo/trial için banner gerek)
      if (this.packageStatus?.urgency) {
        const type = pricingService.getBannerType(this.packageStatus.urgency);
        if (type) return type;
      }
      return null;
    },

    shouldShowBanner() {
      return this.packageStatus?.show === true;
    },

  },
  watch: {
    packageStatus: {
      handler(newStatus) {
        if (newStatus) {
          this.bannerType = this.computedBannerType;
          this.showBanner = this.shouldShowBanner;
        }
      },
      deep: true,
      immediate: true
    }
  },
  created(){
    this.loadCompanyData();
    this.fetchMenuModules();
    this.checkUserPackageStatus();
    this.startBannerStatusCheck();
  },
  beforeUnmount() {
    if (this.bannerCheckInterval) {
      clearInterval(this.bannerCheckInterval);
    }
  },
  methods: {
    async loadCompanyData() {
      this.companyLoading = true;

      const cachedLogo = localStorage.getItem('logoCacheUrl');
      if (cachedLogo) {
        this.companyLogo = cachedLogo;
      }

      try {
        const [companyDetailsResponse, companyListResponse] = await Promise.all([
          settingsService.getCompanySettings(),
          companyService.changeCompanyList()
        ]);

        // Company details
        const newLogoUrl = companyDetailsResponse.data.data.appIconUrl;
        if (newLogoUrl !== cachedLogo) {
          localStorage.setItem('logoCacheUrl', newLogoUrl);
          this.companyLogo = newLogoUrl;
        }
        this.companyName = companyDetailsResponse.data.data.name;

        const user = JSON.parse(localStorage.getItem('user'));

        if (user && companyDetailsResponse.companyId) {
          user.companyId = companyDetailsResponse.companyId;
          localStorage.setItem('user', JSON.stringify(user));
        }

        // Company list
        this.companyItems = companyListResponse.data.data;
        this.filteredCompanyItems = [...this.companyItems];

      } catch (error) {
        handleErrorResponse(error, this.$snackbar);
      } finally {
        this.companyLoading = false;
      }
    },
    async loadPricingData() {
      try {
        const [packagesResponse, userStatusResponse] = await Promise.all([
          pricingService.getPackages(),
          pricingService.getUserPackageStatus()
        ]);

        if (packagesResponse.data.responseType === 'success') {
          this.availablePackages = packagesResponse.data.data || [];
        }

        if (userStatusResponse.data.responseType === 'success') {
          this.packageStatus = userStatusResponse.data.data || null;

          if (this.packageStatus?.urgency) {
            this.pricingModalUrgency = this.packageStatus.urgency;
          }
        }

      } catch (error) {
        this.availablePackages = [];
        this.packageStatus = null;
      }
    },

    async openPricingModal() {
      try {
        const [packagesResponse, currentPlanResponse] = await Promise.all([
          pricingService.getPackages(),
          pricingService.getCurrentPlan()
        ]);

        this.availablePackages = packagesResponse.data.data || [];

        const currentPackageId =
            currentPlanResponse.data.data?.package?.packageId ||
            currentPlanResponse.data.data?.packageId ||
            currentPlanResponse.data?.package?.packageId ||
            currentPlanResponse.data?.packageId;

        this.packageStatus = {
          ...packagesResponse.data.packageStatus,
          currentPackageId: currentPackageId
        };

        this.isPricingModalVisible = true;

      } catch (error) {
      }
    },

    closePricingModal() {
      this.isPricingModalVisible = false;
      this.pricingModalUrgency = 'normal';
      this.availablePackages = [];
    },

    onPackageUpgraded(upgradeData) {

      this.showBanner = false;

      this.checkUserPackageStatus();

      this.closePricingModal();

      this.$snackbar?.add({
        type: 'success',
        text: this.$t('package_successfully_updated')
      });
      },

    handleBannerClose(type) {
      this.showBanner = false;
      localStorage.setItem(`banner_closed_${type}`, new Date().toISOString());
    },

    async checkUserPackageStatus() {
      try {
        const response = await pricingService.getPackages();
        this.packageStatus = response.data.packageStatus;
        const bannerType = this.computedBannerType;
        if (bannerType) {
          const closedDate = localStorage.getItem(`banner_closed_${bannerType}`);
          if (closedDate) {
            const today = new Date().toDateString();
            const closedDateObj = new Date(closedDate).toDateString();
            if (today === closedDateObj) {
              this.showBanner = false;
              return;
            }
          }
        }

      } catch (error) {
        this.showBanner = false;
      }
    },

    startBannerStatusCheck() {
      this.bannerCheckInterval = setInterval(() => {
        this.checkUserPackageStatus();
      }, 5 * 60 * 1000);
    },

    fetchMenuModules() {
      this.isLoading = true;

      roleService.getMenuModules().then((response) => {
        this.allowedIds = response.data.data.map(item => item.key);
      }).catch(error => {
        handleErrorResponse(error, this.$snackbar);
      }).finally(() => {
        this.isLoading = false;
      });
    },
    filterCompanyItems() {
      if (!this.searchQuery) {
        this.filteredCompanyItems = [...this.companyItems];
      } else {
        const query = this.searchQuery.toLowerCase().trim();
        this.filteredCompanyItems = this.companyItems.filter(item =>
            item.name.toLowerCase().includes(query)
        );
      }
    },
    changeCompany(item){
      this.companyLoading = true;
      this.companyDropdown = false;

      companyService.changeCompany(item.companyId).then(response => {
        if (response.data.responseType === "success") {
          this.getCompanyDetails();
          this.checkUserPackageStatus();

          const user = JSON.parse(localStorage.getItem('user'));
          if (user) {
            user.companyId = item.companyId;
            localStorage.setItem('user', JSON.stringify(user));
          }

          window.location.reload();
        } else {
          this.$snackbar.add({
            type: 'error',
            text: this.$t('company_switch_failed')
          });
        }
      }).catch(error => {
        handleErrorResponse(error, this.$snackbar);
      }).finally(() => {
        this.companyLoading = false;
      });

    },
    toggleCompanyDropdown() {
      this.companyDropdown = !this.companyDropdown;

      if (this.companyDropdown && this.searchEnabled) {
        this.searchQuery = '';
        this.filteredCompanyItems = [...this.companyItems];

        this.$nextTick(() => {
          if (this.$refs.searchInput) {
            this.$refs.searchInput.focus();
          }
        });
      }
    },
    hideCompanyDropdown() {
      this.companyDropdown = false;
    },
    handleGoMessages() {
      this.$router.push('/messages');
    },
    updateLanguage(lang) {
      this.$i18n.locale = lang;
      localStorage.setItem('userLanguage', lang);
      window.location.reload();
    },
    getCompanies(){
      companyService.changeCompanyList().then(response => {
        this.companyItems = response.data.data;
        this.filteredCompanyItems = [...this.companyItems];
      }).catch(error => {
        handleErrorResponse(error, this.$snackbar);
      }).finally(() => {
        this.companyLoading = false;
      });
    },
    getCompanyDetails() {
      const cachedLogo = localStorage.getItem('logoCacheUrl')
      if (cachedLogo) {
        this.companyLogo = cachedLogo;
      }

      settingsService.getCompanySettings().then(response => {
        const newLogoUrl = response.data.data.appIconUrl;

        if (newLogoUrl !==  cachedLogo) {
          localStorage.setItem('logoCacheUrl', newLogoUrl)
          this.companyLogo = newLogoUrl;
        }

        if (response.data?.data?.companyId) {
          const user = JSON.parse(localStorage.getItem('user'));
          if (user) {
            user.companyId = response.data.data.companyId;
            localStorage.setItem('user', JSON.stringify(user));
          }
        }

        this.companyName = response.data.data.name;
      }).catch(error => {
      });
    },
    toggleMenu() {
      this.isMenuOpen = !this.isMenuOpen;
    },
    getMenuRoutes(type) {
      if (!this.menuRoutes) {
        return [];
      }
      if (!this.menuRoutes[type]) {
        return [];
      }

      if (this.allowedIds.length === 0) {
        return this.menuRoutes[type];
      }

      return this.menuRoutes[type].filter(item => {
        return this.allowedIds.includes(item.id);
      });
    },
    checkActive(route) {
      try {
        const currentUrl = new URL(window.location.href);
        const pathnameParts = currentUrl.pathname.split('/');
        const mainPath = '/' + pathnameParts[1];
        return mainPath === route;
      } catch (error) {
        return false;
      }
    },
    checkDropdownActive(routeArray) {
      try {
        const currentUrl = new URL(window.location.href);
        const pathnameParts = currentUrl.pathname.split('/');
        const mainPath = '/' + pathnameParts[1];
        return routeArray.some(route => mainPath === route);
      } catch (error) {
        return false;
      }
    },
    onLogout() {
      authService.logout().then(() => {
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        this.$router.push('/login');
      }).catch(() => {
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
        this.$router.push('/login');
      });
    },
    handleMenuClickOutside() {
      this.isMenuOpen = false;
    },

    closePaymentModal() {
      this.isPaymentModalVisible = false;
      this.selectedPackageForPayment = null;
    },

    onPaymentSuccess(paymentData) {
      this.showBanner = false;

      this.checkUserPackageStatus();

      this.closePaymentModal();

      this.$snackbar?.add({
        type: 'success',
        text: this.$t('payment_successfully_completed')
      });
      },

    goToHome() {
      this.$router.push('/');
    },

  }
}
</script>

<style scoped>

.modal-overlay {
  z-index: 10000 !important;
}

.header-container {
  position: sticky;
  top: 0;
  z-index: 1000;
}

.header-view {
  display: flex;
  flex-direction: row;
  justify-content: space-evenly;
  align-items: center;
  padding: 0 20px;
  background-color: #fcfcfd;
  min-height: 96px;
  transition: background-color 0.3s, backdrop-filter 0.3s, min-height 0.3s;
}

.header-view.is-scrolled {
  background-color: rgba(255, 255, 255, 0.5);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
  min-height: 72px;
}

.header-view .left{
  display: flex;
  flex: 1;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  gap: 15px;
}


.header-view .right {
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;
  flex: 1;
}

.header-view .center {
  display: flex;
  flex: 1;
  justify-content: center;
  align-items: center;
}

.company-dropdown-wrapper {
  position: relative;
  display: inline-block;
  z-index: 9999;
}

.company-dropdown {
  display: flex;
  align-items: center;
  cursor: pointer;
  gap: 5px;
  padding: 10px;
  font-family: euclid_medium, sans-serif;
  font-size: 12px;
  line-height: 12px;
  justify-content: center;
  height: 36px;
  border-radius: 9px;
  border: 1px solid #EAECF0;
  background-color: #fff;
  transition: background 0.2s;
}

.company-dropdown:hover {
  background-color: #F5F5FF;
}

.dropdown-content {
  position: absolute;
  background-color: #FFFFFF;
  border: 1px solid #D0D5DD;
  min-width: 160px;
  padding: 10px;
  border-radius: 12px;
  z-index: 1;
  top: calc(100% + 15px);
  right: 0;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.search-box {
  margin-bottom: 8px;
}

.search-input {
  width: 100%;
  padding: 6px 8px;
  border: 1px solid #D0D5DD;
  border-radius: 6px;
  font-family: euclid_regular, sans-serif;
  font-size: 12px;
  outline: none;
}

.list {
  display: flex;
  flex-direction: column;
  gap: 5px;
  max-height: 200px;
  overflow-y: auto;
}

.item {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  font-family: euclid_medium, sans-serif;
  font-size: 12px;
  padding: 7px 15px;
  border-radius: 8px;
  color: #000000;
}

.item:hover {
  background-color: #F5F5FF;
}

.no-results {
  display: flex;
  justify-content: center;
  padding: 7px;
  font-family: euclid_regular, sans-serif;
  font-size: 12px;
  color: #6C737F;
}

.dropdown-loading-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
}

.arrow-down {
  width: 16px;
  height: 16px;
  transition: transform 0.3s;
}

.region-flag {
  width: 18px;
  height: 14px;
  border-radius: 2px;
  object-fit: cover;
}

.rotate {
  transform: rotate(180deg);
}

.menu-icon {
  display: flex;
  justify-content: center;
  align-items: center;
  width: 36px;
  height: 36px;
  cursor: pointer;
  border-radius: 9px;
  border: 1px solid #EAECF0;
  background-color: #fff;
  transition: background 0.2s;
}

.menu-icon:hover {
  background-color: #F5F7FF;
}

.logo{
  height: 32px;
  width: auto;
  cursor: pointer;
}

.menu-wrapper {
  position: relative;
}

.menu-view {
  position: absolute;
  padding: 15px;
  background-color: #FFFFFF;
  border: 1px solid #D0D5DD;
  border-radius: 12px;
  top: calc(100% + 15px);
  right: 0;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.menu-user {
  display: flex;
  flex-direction: row;
  gap: 10px;
  margin-bottom: 10px;
}

.menu-user-left {
  display: flex;
  justify-content: center;
  align-items: center;

}

.menu-user-end{
  display: flex;
  flex-direction: row;
  gap: 5px;
}

.menu-user-right {
  display: flex;
  flex: 1;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 5px;
}

.user-avatar {
  width: 42px;
  height: 42px;
  border-radius: 50%;
  border: 1px solid #EAECF0;
}

.menu-splitter {
  width: 100%;
  height: 1px;
  background-color: #EAECF0;
}

.menu-item {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  padding: 7px 10px;
  border-radius: 9px;
  min-width: 190px;
  transition: background 0.3s;
}

.menu-item.active {
  background-color: #F5F5FF;
}

.menu-item.active span {
  font-family: euclid_medium, sans-serif;
  font-size: 14px;
  color: #1C3CD5;
}

.menu-item img {
  width: 18px;
  height: 18px;
}

.menu-item span {
  font-family: euclid_regular, sans-serif;
  font-size: 13px;
  line-height: 13px;
  color: #344054;
  text-wrap: nowrap;
}

.menu-item.logout span {
  font-family: euclid_medium, sans-serif;
  font-size: 14px;
  color: #D92D2A;
}

.logout-button {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 5px 10px;
  border-radius: 8px;
  cursor: pointer;
  font-family: euclid_regular, sans-serif;
  font-size: 12px;
  color: #D92D2A;
  transition: background 0.3s;
}

.logout-button:hover {
  background-color: #ffefee;
}

.menu-item:hover {
  background-color: #F5F5FF;
}

.menu-user-name {
  font-family: euclid_medium, sans-serif;
  font-size: 14px;
  line-height: 14px;
  color: #0a0f36;
}

.menu-user-email {
  font-family: euclid_regular, sans-serif;
  font-size: 12px;
  line-height: 12px;
  color: #6C737F;
}

.menu-user-action {
  font-family: euclid_regular, sans-serif;
  font-size: 12px;
  line-height: 12px;
  color: #1C3CD5;
  cursor: pointer;
}

.menu-user-action:hover {
  text-decoration: underline;
}

.route-link {
  text-decoration: none;
  margin: 0;
  padding: 0;
}

.route-link.text {
  line-height: 12px;
}

.menu-item-header {
  font-family: euclid_semi_bold, sans-serif;
  font-size: 14px;
  line-height: 14px;
  color: #344054;
  padding: 10px 0;
  margin-bottom: 5px;
  border-bottom: 1px solid #EAECF0;
}

.menu-row {
  display: flex;
  flex-direction: row;
  gap: 20px;
}

.menu-row-left, .menu-row-right  {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.menu-footer {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
}

.horizontal-menu {
  display: flex;
  flex-direction: row;
  gap: 5px;
  margin-left: 10px;
}

.horizontal-menu-item {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 5px;
  cursor: pointer;
  padding: 7px 10px;
  border-radius: 9px;
}

.horizontal-menu-item span {
  font-family: euclid_medium, sans-serif;
  font-size: 16px;
  line-height: 16px;
  color: #aaacaf;
  text-wrap: nowrap;
  transition: font-size 0.3s, color 0.3s;
}


.header-view.is-scrolled .horizontal-menu-item span {
  font-size: 14px;
}


.horizontal-menu-item.active span {
  font-family: euclid_medium, sans-serif;
  font-size: 16px;
  line-height: 16px;
  color: #101828;
}

.horizontal-menu-icon {
  width: 18px;
  height: 18px;
}

.horizontal-menu-arrow-down {
  width: 12px;
  height: 12px;
  transition: transform 0.3s;
  margin-left: 3px;
}

@media (max-width: 996px) {
  .horizontal-menu {
    display: none;
  }
}
</style>
